/**
 * App Layout Component
 *
 * Main layout orchestrator that switches between desktop and mobile layouts.
 */

import { BottomNav, MobileDrawer } from './components/mobile';
import { MobileHeader } from './components/header';

import type { AppLayoutProps } from './types';
import { SearchModal } from './components/common';
import { Sidebar } from './components/sidebar';
import { cn } from '@/shared/lib';
import { useEffect } from 'react';
import { useUIStore } from '@/state/stores/ui.store';

export const AppLayout = ({ children }: AppLayoutProps) => {
  const { isMobile, isTablet, setScreenWidth, isSidebarCollapsed } = useUIStore();

  // Update screen width on resize
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    // Set initial value
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setScreenWidth]);

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-950">
        <MobileHeader />

        <main className="pt-14 pb-20">{children}</main>

        <BottomNav />
        <MobileDrawer />
        <SearchModal />
      </div>
    );
  }

  // Tablet Layout (collapsed sidebar)
  if (isTablet) {
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-950 flex">
        <Sidebar />

        <div
          className={cn(
            // See desktop branch — min-w-0 keeps horizontal overflow
            // (e.g. an overflow-x-auto tabs strip) contained inside this
            // column instead of pushing the whole page sideways.
            'flex-1 min-w-0 flex flex-col transition-all duration-300',
            'ml-[72px]' // Always collapsed on tablet
          )}
        >
          <main className="flex-1 p-4 lg:p-6">{children}</main>
        </div>

        <SearchModal />
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 flex">
      <Sidebar />

      <div
        className={cn(
          // min-w-0 prevents this flex item from stretching to fit
          // intrinsic-min content (e.g. an overflow-x-auto tab strip)
          // and pushing the whole page horizontally past the viewport.
          'flex-1 min-w-0 flex flex-col transition-all duration-300',
          isSidebarCollapsed ? 'ml-[72px]' : 'ml-64'
        )}
      >
        <main className="flex-1 p-0">{children}</main>
      </div>

      <SearchModal />
    </div>
  );
};

export default AppLayout;
